import { useQuery, useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Sparkles, Check, ChevronRight, Trophy, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { useLocation } from 'wouter';
import type { EnrichedPendingKudoz, User } from '@shared/schema';
const kudozIcon = "/kudoz-icon-disc.png";
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useUserProfileOptional } from '@/hooks/useUserProfile';
import { useKudozPending } from '@/hooks/useKudozPending';

const TIER_THRESHOLDS = {
  bronze: { min: 0, max: 20000, next: 'Silver' },
  silver: { min: 20000, max: 40000, next: 'Gold' },
  gold: { min: 40000, max: 80000, next: 'Platinum' },
  platinum: { min: 80000, max: Infinity, next: null }
};

function getTierFromLifetime(lifetimeKudoz: number): { tier: string; color: string } {
  if (lifetimeKudoz >= 80000) return { tier: 'Platinum', color: 'bg-gradient-to-r from-slate-400 to-slate-300 text-slate-900' };
  if (lifetimeKudoz >= 40000) return { tier: 'Gold', color: 'bg-gradient-to-r from-yellow-500 to-amber-400 text-yellow-950' };
  if (lifetimeKudoz >= 20000) return { tier: 'Silver', color: 'bg-gradient-to-r from-gray-400 to-gray-300 text-gray-800' };
  return { tier: 'Bronze', color: 'bg-gradient-to-r from-amber-700 to-amber-600 text-amber-100' };
}

function getTierProgress(lifetimeKudoz: number): { current: number; target: number; percentage: number; nextTier: string | null } {
  const tierKey = lifetimeKudoz >= 80000 ? 'platinum' : 
                  lifetimeKudoz >= 40000 ? 'gold' : 
                  lifetimeKudoz >= 20000 ? 'silver' : 'bronze';
  
  const tier = TIER_THRESHOLDS[tierKey];
  
  if (tierKey === 'platinum') {
    return { current: lifetimeKudoz, target: tier.max, percentage: 100, nextTier: null };
  }
  
  const progressInTier = lifetimeKudoz - tier.min;
  const tierRange = tier.max - tier.min;
  const percentage = Math.min(100, (progressInTier / tierRange) * 100);
  
  return { 
    current: lifetimeKudoz, 
    target: tier.max, 
    percentage, 
    nextTier: tier.next 
  };
}

export function KudozSection() {
  const [, navigate] = useLocation();
  const [claimingAll, setClaimingAll] = useState(false);
  const [claimedIds, setClaimedIds] = useState<Set<string>>(new Set());
  const [celebrating, setCelebrating] = useState(false);
  const { toast } = useToast();
  
  const userProfile = useUserProfileOptional();

  // Shared hook — deduplicates the /api/kudoz/pending network request with
  // AppSidebar and BottomNavigation via TanStack Query's cache.
  const { pendingKudoz, hasPending, isLoading: isLoadingPending } = useKudozPending();

  const { data: user } = useQuery<User>({ queryKey: ['/api/user'] });

  
  interface ConsolidatedReward {
    id: string;
    type: 'review' | 'order' | 'badge' | 'other';
    restaurantName: string | null;
    totalAmount: number;
    entryIds: string[];
    entries: EnrichedPendingKudoz[];
    badgeName?: string;
    actionName?: string;
  }
  
  const consolidatedRewards = useMemo((): ConsolidatedReward[] => {
    const grouped = new Map<string, ConsolidatedReward>();
    
    for (const pk of pendingKudoz) {
      const metadata = pk.metadata as { reviewId?: string; orderId?: string; badgeName?: string } | null;
      const actionType = pk.action?.actionType || '';
      
      let groupKey: string;
      let type: 'review' | 'order' | 'badge' | 'other';
      
      if (metadata?.reviewId && actionType.startsWith('review')) {
        groupKey = `review_${metadata.reviewId}`;
        type = 'review';
      } else if (metadata?.orderId && actionType.startsWith('order')) {
        groupKey = `order_${metadata.orderId}`;
        type = 'order';
      } else if (actionType === 'badge_earned') {
        groupKey = pk.id;
        type = 'badge';
      } else if (actionType === 'first_review' && metadata?.reviewId) {
        groupKey = `review_${metadata.reviewId}`;
        type = 'review';
      } else {
        groupKey = pk.id;
        type = 'other';
      }
      
      const existing = grouped.get(groupKey);
      if (existing) {
        existing.totalAmount += pk.amount;
        existing.entryIds.push(pk.id);
        existing.entries.push(pk);
        if (pk.restaurant?.name && !existing.restaurantName) {
          existing.restaurantName = pk.restaurant.name;
        }
      } else {
        grouped.set(groupKey, {
          id: groupKey,
          type,
          restaurantName: pk.restaurant?.name || null,
          totalAmount: pk.amount,
          entryIds: [pk.id],
          entries: [pk],
          badgeName: metadata?.badgeName,
          actionName: pk.action?.displayName,
        });
      }
    }
    
    return Array.from(grouped.values()).sort((a, b) => {
      const aTime = a.entries[0]?.createdAt ? new Date(a.entries[0].createdAt).getTime() : 0;
      const bTime = b.entries[0]?.createdAt ? new Date(b.entries[0].createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [pendingKudoz]);

  interface ClaimResponse {
    transaction?: unknown;
    newBalance?: number;
    externalBalance?: number;
    externalBalanceVerified?: boolean;
  }

  const claimAllMutation = useMutation({
    mutationFn: async () => {
      setClaimingAll(true);
      const results: { id: string; success: boolean; amount: number }[] = [];
      let lastExternalBalance: number | undefined;
      let lastVerified = false;
      
      for (const pk of pendingKudoz) {
        try {
          const response = await apiRequest('POST', `/api/kudoz/claim/${pk.id}`);
          const result = response as ClaimResponse;
          
          if (result.externalBalance !== undefined) {
            lastExternalBalance = result.externalBalance;
            lastVerified = result.externalBalanceVerified ?? false;
          }
          
          setClaimedIds(prev => new Set(Array.from(prev).concat(pk.id)));
          results.push({ id: pk.id, success: true, amount: pk.amount });
        } catch (error) {
          results.push({ id: pk.id, success: false, amount: pk.amount });
        }
      }
      
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      const totalClaimed = results.filter(r => r.success).reduce((sum, r) => sum + r.amount, 0);
      
      return { successCount, failCount, totalClaimed, lastExternalBalance, lastVerified };
    },
    onSuccess: async (data) => {
      if (data?.lastExternalBalance !== undefined && userProfile?.updateKudozBalance) {
        if (data.lastVerified) {
          console.log('[KudozSection] Updating Kudoz balance from verified claim response:', data.lastExternalBalance);
          userProfile.updateKudozBalance(data.lastExternalBalance);
        } else {
          console.log('[KudozSection] Updating with unverified balance, will refresh:', data.lastExternalBalance);
          userProfile.updateKudozBalance(data.lastExternalBalance);
          if (userProfile.refreshKudozBalance) {
            setTimeout(() => userProfile.refreshKudozBalance(), 1000);
          }
        }
      } else if (userProfile?.refreshKudozBalance) {
        console.log('[KudozSection] No external balance, refreshing from API');
        setTimeout(() => userProfile.refreshKudozBalance(), 500);
      }
      
      queryClient.invalidateQueries({ queryKey: ['/api/kudoz/pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/badges/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/badges/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/api/kudoz/external-balance'] });
      
      if (data.successCount > 0) {
        setCelebrating(true);
        
        if (data.failCount > 0) {
          toast({
            title: `Claimed ${data.totalClaimed} Kudoz`,
            description: `${data.failCount} reward(s) couldn't be claimed. Try again later.`,
            variant: 'default'
          });
        }
        
        setTimeout(() => {
          setClaimingAll(false);
          setCelebrating(false);
          setClaimedIds(new Set());
        }, 1500);
      } else if (data.failCount > 0) {
        toast({
          title: 'Unable to claim rewards',
          description: 'Please try again later.',
          variant: 'destructive'
        });
        setClaimingAll(false);
        setClaimedIds(new Set());
      }
    },
    onError: () => {
      setClaimingAll(false);
      setCelebrating(false);
      setClaimedIds(new Set());
      toast({
        title: 'Unable to claim rewards',
        description: 'Please try again later.',
        variant: 'destructive'
      });
    }
  });

  const getConsolidatedRewardLabel = (reward: ConsolidatedReward): string => {
    const { type, restaurantName, badgeName, actionName } = reward;
    
    switch (type) {
      case 'review':
        return restaurantName ? `Review of ${restaurantName}` : 'Restaurant review';
      case 'order':
        return restaurantName ? `Order from ${restaurantName}` : 'Restaurant order';
      case 'badge':
        return badgeName ? `Badge: ${badgeName}` : 'Badge earned';
      default:
        return actionName || 'Reward';
    }
  };
  
  const isConsolidatedClaimed = (reward: ConsolidatedReward): boolean => {
    return reward.entryIds.every(id => claimedIds.has(id));
  };

  if (isLoadingPending) {
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <img src={kudozIcon} alt="Kudoz" className="h-5 w-5" />
          <h3 className="font-semibold text-lg">Your Kudoz</h3>
        </div>
        <Card className="bg-gradient-to-br from-kudoz/10 to-kudoz/5">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="h-12 bg-muted/50 rounded animate-pulse" />
              <div className="h-8 bg-muted/50 rounded animate-pulse" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const kudozLinked = userProfile?.kudozLinked ?? true;
  const availableBalance = userProfile?.kudozBalance ?? user?.kudozBalance ?? 0;
  const lifetimeEarned = user?.lifetimeKudozEarned || 0;
  const totalPendingAmount = pendingKudoz.reduce((sum, k) => sum + k.amount, 0);
  const hasPendingKudoz = pendingKudoz.length > 0;
  
  const { tier, color: tierColor } = getTierFromLifetime(lifetimeEarned);
  const tierProgress = getTierProgress(lifetimeEarned);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <img src={kudozIcon} alt="Kudoz" className="h-5 w-5" />
        <h3 className="font-semibold text-lg">Your Kudoz</h3>
      </div>

      {!kudozLinked && (
        <div className="flex items-start gap-2.5 rounded-md border border-muted bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground/70" />
          <span>Your Kudoz wallet is being set up. Sign out and back in to complete the link — your rewards are safe.</span>
        </div>
      )}

      <Card 
        className="relative bg-gradient-to-br from-kudoz/10 to-kudoz/5 border-kudoz/30 overflow-hidden"
        data-testid="card-kudoz-unified"
      >
        {/* Celebration overlay (CSS only) */}
        {celebrating && (
          <div
            className="absolute inset-0 pointer-events-none z-10 rounded-lg"
            style={{ background: 'radial-gradient(circle, rgba(13,156,151,0.15) 0%, transparent 70%)' }}
          />
        )}

        <CardContent className="p-4 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <div>
                  <div className="text-xs text-muted-foreground mb-0.5">Available</div>
                  <div className="text-2xl font-bold text-kudoz flex items-center gap-1.5" data-testid="text-available-kudoz">
                    <img src={kudozIcon} alt="" className="h-5 w-5" />
                    {availableBalance.toLocaleString()}
                  </div>
                </div>
                
                {hasPendingKudoz && (
                  <div className="border-l border-kudoz/20 pl-3">
                    <div className="text-xs text-muted-foreground mb-0.5">Ready</div>
                    <div className="text-lg font-semibold text-warning" data-testid="text-pending-kudoz">
                      +{totalPendingAmount.toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <Badge className={cn("text-sm px-2 py-0.5 flex-shrink-0", tierColor)} data-testid="badge-tier">
              {tier}
            </Badge>
          </div>

          {hasPendingKudoz && (
            <div className="border-t border-kudoz/20 pt-3 space-y-2">
              <div className="space-y-0">
                {consolidatedRewards.slice(0, 4).map((reward, index) => {
                  const isClaimed = isConsolidatedClaimed(reward);
                  
                  return (
                    <div
                      key={reward.id}
                      className={`flex items-center justify-between py-2 ${
                        index !== Math.min(consolidatedRewards.length, 4) - 1 ? 'border-b border-kudoz/10' : ''
                      }`}
                    >
                      <span className={`text-sm flex-1 min-w-0 pr-3 ${
                        isClaimed ? 'text-muted-foreground line-through' : 'text-foreground'
                      }`}>
                        {getConsolidatedRewardLabel(reward)}
                      </span>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-sm font-semibold ${
                          isClaimed ? 'text-muted-foreground' : 'text-kudoz'
                        }`}>
                          +{reward.totalAmount}
                        </span>
                        
                        {isClaimed && (
                          <div className="w-5 h-5 rounded-full bg-kudoz flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {consolidatedRewards.length > 4 && (
                  <div className="text-xs text-muted-foreground py-1">
                    +{consolidatedRewards.length - 4} more rewards
                  </div>
                )}
              </div>

              <Button
                className="w-full bg-kudoz hover:bg-kudoz/90 text-white font-semibold h-10"
                onClick={() => claimAllMutation.mutate()}
                disabled={claimAllMutation.isPending || celebrating || !kudozLinked}
                data-testid="button-claim-all-kudoz"
              >
                {claimingAll ? (
                  <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                ) : celebrating ? (
                  <Check className="h-4 w-4 mr-2" />
                ) : (
                  <img src={kudozIcon} alt="" className="h-4 w-4 mr-2" />
                )}
                {celebrating ? 'Claimed!' : claimingAll ? 'Claiming...' : `Claim All (${totalPendingAmount})`}
              </Button>
            </div>
          )}

          <div className="border-t border-kudoz/20 pt-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {tierProgress.nextTier ? `Progress to ${tierProgress.nextTier}` : 'Maximum tier reached'}
              </span>
              {tierProgress.nextTier && (
                <span className="font-medium text-xs">
                  {(tierProgress.target - lifetimeEarned).toLocaleString()} to go
                </span>
              )}
            </div>
            <Progress value={tierProgress.percentage} className="h-1.5" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{lifetimeEarned.toLocaleString()} lifetime</span>
              {tierProgress.nextTier && <span>{tierProgress.target.toLocaleString()}</span>}
            </div>
          </div>

          <Button 
            variant="ghost" 
            className="w-full justify-between h-9 px-2 -mx-2 text-sm hover-elevate"
            onClick={() => navigate('/rewards')}
            data-testid="button-view-rewards"
          >
            <span className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-kudoz" />
              View Rewards & History
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
